import { useQuery } from "@tanstack/react-query";
import { ReactComponent as ChevronLeft } from "assets/svg/chevron-left.svg";
import { ReactComponent as OutlineCopy } from "assets/svg/outline-copy.svg";
import { ReactComponent as OutlineEdit } from "assets/svg/outline-edit-1.svg";
import { ReactComponent as OutlineEyeOpen } from "assets/svg/outline-eye-open.svg";
import { ReactComponent as OutlineInfoCircle } from "assets/svg/outline-info-circle.svg";
import { ReactComponent as OutlinePlus16 } from "assets/svg/outline-plus-16.svg";
import { ReactComponent as OutlineShare } from "assets/svg/outline-share-2.svg";
import { ReactComponent as OutlineTrash } from "assets/svg/outline-trash.svg";
import { ReactComponent as OutlineChevronDown16 } from "assets/svg/outline-chevron-down-16.svg";
import { ReactComponent as OutlineChevronUp16 } from "assets/svg/outline-chevron-up-16.svg";
import ContactSelector from "components/contact-selector/ContactSelector";
import ErrorMessage from "components/error-message";
import IconButton from "components/icon-button";
import Loading from "components/Loading";
import SuccessMessage from "components/success-message";
import { ENABLE_PREBUILT_TEMPLATES } from "constants/featureFlags";
import { usePhonePortraitContext } from "contexts/phone-portrait-context";
import { useSubscription } from "contexts/subscription-context";
import { useTimeOnPageContext } from "contexts/time-on-page";
import useToggleModal from "hooks/useToggleModal";
import useSaveTemplatesMutation from "mutations/useSaveTemplatesMutation";
import getTemplateFromSchema from "notes-and-forms-v2/util/getTemplateFromSchema";
import { listSchemasQueryOptions } from "queries/schemaQueryOptions";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  OverlayTrigger,
  Popover,
  Row,
  Tab,
  Table,
  Tabs,
  Tooltip,
} from "react-bootstrap";
import { Link, useNavigate, useLocation } from "react-router-dom";
import createNoteOrFormData from "services/createNoteOrFormData";
import { useDeleteSchemaMutation } from "services/deleteNoteOrFormSchema";
import { AmplitudePageName, track } from "utils/amplitude";
import { useOrchidPro } from "../contexts/orchid-pro-context";
import AddTemplateModal from "./AddTemplateModal";
import styles from "./NotesAndForms.module.scss";
import PrebuiltTemplatesModal from "./PrebuiltTemplatesModal";
import getClasses from "utils/getClasses";

const ANALYTICS_PAGE_NAME: AmplitudePageName = "pro-notes-and-forms-page";

const SCHEMA_TYPE_CONFIG = {
  progress_note: {
    title: "Progress Notes",
    description:
      "Templates to record the observations, interventions, and outcomes from each appointment",
  },
  chart_note: {
    title: "Chart Notes",
    description: "Templates for documenting clinical chart notes",
  },
  other_documents: {
    title: "Other Documents",
    description:
      "Templates for documentation that's not related to specific appointments",
  },
  intake_form: {
    title: "Intake Forms",
    description:
      "Templates to gather information from clients via the Client Portal",
  },
  assessment: {
    title: "Assessments",
    description:
      "Assessments are the forms that are automatically sent to your clients",
  },
  other: {
    title: "Other",
    description: "Additional templates for various documentation needs",
  },
} as const;

const groupTemplatesBySchemaType = (templates: NoteOrFormTemplate[]) => {
  return templates.reduce((acc, template) => {
    const type = template.schemaType || "other";
    if (type === "chart_note") return acc;
    
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(template);
    return acc;
  }, {} as Record<string, NoteOrFormTemplate[]>);
};

// TODO: need to refactor
const NotesAndForms = () => {
  const { orchidPro } = useOrchidPro();
  const { isPhonePortrait } = usePhonePortraitContext();

  const { planType } = useSubscription();

  const navigate = useNavigate();

  const { setPage } = useTimeOnPageContext();
  useEffect(() => {
    setPage(ANALYTICS_PAGE_NAME);
  }, [setPage]);

  const location = useLocation();
  const { activeTab } = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return {
      activeTab: query.get("tab") || "",
    };
  }, [location.search]);

  const [error, setError] = useState<any>();
  const [successMessage, setSuccessMessage] = useState<string>();
  const [tab, setTab] = useState(activeTab || "note-template");
  const [formToShare, setFormToShare] = useState<number | null>(null);
  const [showPrebuiltTemplates, setShowPrebuiltTemplates] = useState(false);
  const [highlightedTemplates, setHighlightedTemplates] = useState<
    NoteOrFormTemplate[]
  >([]);

  const {
    open: openContactSelector,
    close: closeContactSelector,
    show: showContactSelector,
  } = useToggleModal(false);

  const {
    open: openAddTemplate,
    close: closeAddTemplate,
    show: showAddTemplate,
  } = useToggleModal(false);

  const type = useMemo(() => {
    switch (tab) {
      case "note-template":
        return "note";
      case "form-template":
        return "form";
      case "assessments":
        return "assessment";
      default:
        return "note";
    }
  }, [tab]);

  const listSchemasQuery = useQuery(listSchemasQueryOptions(type));
  const templates = useMemo(
    () => listSchemasQuery.data?.map(getTemplateFromSchema) ?? [],
    [listSchemasQuery.data],
  );

  const saveTemplatesMutation = useSaveTemplatesMutation();
  const deleteSchemaMutation = useDeleteSchemaMutation();

  const filteredSchemas = useMemo(() => {
    switch (type) {
      case "note":
        return templates;
      case "form": {
        if (planType === "ai-only") {
          return templates.filter(
            (el) =>
              el.title === "Consent to Use AI Scribe During Medical Encounters",
          );
        } else {
          return templates;
        }
      }
      case "assessment": {
        return templates.filter((el) => {
          const weeklyIds = [
            4000000000000000, 4000000000000003, 4000000000000006,
            4000000000000012,
          ];
          const biWeeklyIds = [
            4000000000000001, 4000000000000004, 4000000000000007,
            4000000000000013,
          ];
          const monthlyIds = [
            4000000000000002, 4000000000000005, 4000000000000008,
            4000000000000014,
          ];
          const afterAppointmentIds = [
            4000000000000009, 4000000000000010, 4000000000000011,
            4000000000000015,
          ];

          switch (orchidPro?.measurement_settings?.frequency) {
            case "weekly":
              return weeklyIds.includes(el.id);
            case "bi-weekly":
              return biWeeklyIds.includes(el.id);
            case "monthly":
              return monthlyIds.includes(el.id);
            case "after_every_session":
              return afterAppointmentIds.includes(el.id);
            default:
              return weeklyIds.includes(el.id); // Default to weekly if no frequency set
          }
        });
      }
      default:
        return [];
    }
  }, [type, templates, planType, orchidPro?.measurement_settings?.frequency]);

  const handleContactSelected = useCallback(
    async (arg) => {
      if (!formToShare) return;
      try {
        await createNoteOrFormData("form", {
          form_schema_id: formToShare,
          patient_cognito_sub: arg.orchid_user_id,
        });
        setSuccessMessage("Shared");
      } catch (error) {
        setError(error);
        // TODO: better to show error in modal
        closeContactSelector();
        return;
      }
      closeContactSelector();
    },
    [closeContactSelector, formToShare],
  );

  useEffect(() => {
    if (listSchemasQuery.error) {
      setError(listSchemasQuery.error.message);
      return;
    }
  }, [listSchemasQuery.error]);

  const [expandedTypes, setExpandedTypes] = useState(
    new Set<string>(["progress_note", "intake_form", "other_document"]),
  );

  return (
    <>
      {showPrebuiltTemplates && (
        <PrebuiltTemplatesModal
          show={showPrebuiltTemplates}
          onHide={() => setShowPrebuiltTemplates(false)}
          templateType={tab === "note-template" ? "note" : "form"}
          onTemplatesImported={setHighlightedTemplates}
        />
      )}
      {successMessage && (
        <SuccessMessage
          show={true}
          onHide={() => setSuccessMessage(undefined)}
          message={successMessage}
          duration={1000}
        />
      )}
      {isPhonePortrait && (
        <div className={styles["phone-header"]}>
          <div className={styles["phone-header__title"]}>
            <IconButton onClick={() => navigate(-1)}>
              <ChevronLeft />
            </IconButton>
            <div>Template Library</div>
          </div>
          <Tabs
            defaultActiveKey="note-template"
            id="uncontrolled-tab-notesforms"
            className={styles["phone-header__tabs"]}
            onSelect={(eventKey) => {
              track(ANALYTICS_PAGE_NAME, `${eventKey}-tab`, "click");
              setTab(eventKey || "note-template");
            }}
            activeKey={tab}
          >
            <Tab eventKey="note-template" title={"Clinical Notes"}></Tab>

            <Tab eventKey="form-template" title={"Forms"}></Tab>
          </Tabs>
        </div>
      )}
      <div
        className={
          !isPhonePortrait
            ? styles["notes-and-forms"]
            : styles["notes-and-forms--phone"]
        }
      >
        {!isPhonePortrait && (
          <div className={styles["notes-and-forms__top"]}>
            <h2 className="questrial deep-blue">
              Template Library
              <OverlayTrigger
                trigger={["hover", "focus"]}
                placement="right"
                overlay={
                  <Popover id="popover-session-type">
                    <Popover.Content>
                      Legal Disclaimer: Templates are for reference only. It is
                      your responsibility to customize them and ensure they meet
                      the legal requirements of your state.
                    </Popover.Content>
                  </Popover>
                }
              >
                <OutlineInfoCircle className={"deep-blue ml-2"} />
              </OverlayTrigger>
            </h2>
            <Row className="justify-content-between">
              <Tabs
                defaultActiveKey="note-template"
                id="uncontrolled-tab-notesforms"
                className={styles["notes-and-forms__tabs"]}
                onSelect={(eventKey) => {
                  track(ANALYTICS_PAGE_NAME, `${eventKey}-tab`, "click");
                  setTab(eventKey || "note-template");
                  navigate(
                    `/pro-dashboard/notes-and-forms?tab=${eventKey}`,
                    { replace: true }
                  );
                }}
                activeKey={tab}
              >
                <Tab eventKey="note-template" title={"Clinical Notes"}></Tab>
                <Tab eventKey="form-template" title={"Forms"}></Tab>

                {planType !== "ai-only" && (
                  <Tab eventKey="assessments" title={"Assessments"}></Tab>
                )}
              </Tabs>
            </Row>
          </div>
        )}
        <div className={styles["notes-and-forms__bottom"]}>
          {listSchemasQuery.isLoading && <Loading />}

          {listSchemasQuery.isSuccess && (
            <Table
              responsive
              bordered
              className="align-items-center table-flush"
            >
              {Object.entries(groupTemplatesBySchemaType(filteredSchemas)).map(
                ([schemaType, templates]) => (
                  <React.Fragment key={schemaType}>
                    <thead>
                      <tr
                        className={styles["template-type-header"]}
                        onClick={() => {
                          setExpandedTypes((prev) => {
                            const newSet = new Set(prev);
                            if (newSet.has(schemaType)) {
                              newSet.delete(schemaType);
                            } else {
                              newSet.add(schemaType);
                            }
                            return newSet;
                          });
                        }}
                        style={{ cursor: "pointer" }}
                      >
                        <th colSpan={tab === "assessments" ? 2 : 5}>
                          <div
                            className={styles["notes-and-forms__title-section"]}
                          >
                            <div
                              className={
                                styles["notes-and-forms__title-section-left"]
                              }
                            >
                              {expandedTypes.has(schemaType) ? (
                                <OutlineChevronDown16
                                  className={styles["chevron-icon"]}
                                />
                              ) : (
                                <OutlineChevronUp16
                                  className={styles["chevron-icon"]}
                                />
                              )}
                              <div>
                                <div
                                  className={
                                    styles[
                                      "notes-and-forms__title-section-title"
                                    ]
                                  }
                                >
                                  {schemaType
                                    .toLowerCase()
                                    .replace(" ", "_") === "other_document" &&
                                  type === "assessment"
                                    ? SCHEMA_TYPE_CONFIG["assessment"].title
                                    : SCHEMA_TYPE_CONFIG[
                                        schemaType
                                          .toLowerCase()
                                          .replace(
                                            " ",
                                            "_",
                                          ) as keyof typeof SCHEMA_TYPE_CONFIG
                                      ]?.title || schemaType}
                                </div>
                                <div
                                  className={
                                    styles[
                                      "notes-and-forms__title-section-text"
                                    ]
                                  }
                                >
                                  {schemaType
                                    .toLowerCase()
                                    .replace(" ", "_") === "other_document" &&
                                  type === "assessment"
                                    ? SCHEMA_TYPE_CONFIG["assessment"]
                                        .description
                                    : SCHEMA_TYPE_CONFIG[
                                        schemaType
                                          .toLowerCase()
                                          .replace(
                                            " ",
                                            "_",
                                          ) as keyof typeof SCHEMA_TYPE_CONFIG
                                      ]?.description}
                                </div>
                              </div>
                            </div>
                            <div
                              className={
                                styles["notes-and-forms__title-section-right"]
                              }
                            >
                              {tab !== "assessments" && (
                                <Button
                                  variant={"link"}
                                  to={
                                    ENABLE_PREBUILT_TEMPLATES
                                      ? {}
                                      : {
                                          pathname: `/pro-dashboard/notes-and-forms/edit-template/${
                                            tab === "note-template"
                                              ? "note"
                                              : "form"
                                          }`,
                                        }
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (ENABLE_PREBUILT_TEMPLATES) {
                                      openAddTemplate();
                                    }
                                    track(
                                      ANALYTICS_PAGE_NAME,
                                      "add-new-button",
                                      "click",
                                      {
                                        eventProperties: {
                                          type:
                                            tab === "note-template"
                                              ? "note"
                                              : "form",
                                        },
                                      },
                                    );
                                  }}
                                  as={Link as any}
                                  className={
                                    styles[
                                      "notes-and-forms__title-section-button"
                                    ]
                                  }
                                >
                                  <OutlinePlus16 />
                                  Add New
                                </Button>
                              )}
                            </div>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {expandedTypes.has(schemaType) &&
                        templates.map((template) => (
                          <tr
                            key={template.id}
                            className={getClasses(
                              highlightedTemplates.some(
                                (t) =>
                                  t.id === template.id &&
                                  t.type === template.type,
                              ) && styles.highlighted,
                            )}
                          >
                            <td className="rg-16">{template.title}</td>
                            {tab !== "assessments" ? (
                              <>
                                <td className={styles["template-option-td"]}>
                                  <OverlayTrigger
                                    placement="top"
                                    overlay={
                                      <Tooltip id="button-tooltip">
                                        Edit
                                      </Tooltip>
                                    }
                                  >
                                    <Link
                                      to={{
                                        pathname: `/pro-dashboard/notes-and-forms/edit-template/${template.type}/${template.id}`,
                                      }}
                                      onClick={() => {
                                        track(
                                          ANALYTICS_PAGE_NAME,
                                          `edit-${template.type}-button`,
                                          "click",
                                        );
                                      }}
                                    >
                                      <OutlineEdit color={`var(--gray01)`} />
                                    </Link>
                                  </OverlayTrigger>
                                </td>
                                <td className={styles["template-option-td"]}>
                                  <OverlayTrigger
                                    placement="top"
                                    overlay={
                                      <Tooltip id="button-tooltip">
                                        Duplicate
                                      </Tooltip>
                                    }
                                  >
                                    <IconButton
                                      className="note-template"
                                      onClick={() => {
                                        track(
                                          ANALYTICS_PAGE_NAME,
                                          `copy-${template.type}-button`,
                                          "click",
                                        );
                                        saveTemplatesMutation.mutate(
                                          [
                                            {
                                              type: template.type,
                                              title: template.title,
                                              elements: template.elements,
                                            },
                                          ],
                                          {
                                            onSuccess: (data) => {
                                              setHighlightedTemplates(
                                                data.map(getTemplateFromSchema),
                                              );
                                              setSuccessMessage("Duplicated!");
                                            },
                                            onError: setError,
                                          },
                                        );
                                      }}
                                    >
                                      <OutlineCopy color={`var(--gray01)`} />
                                    </IconButton>
                                  </OverlayTrigger>
                                </td>
                                {tab === "form-template" && (
                                  <td className={styles["template-option-td"]}>
                                    <OverlayTrigger
                                      placement="top"
                                      overlay={
                                        <Tooltip id="button-tooltip">
                                          Share
                                        </Tooltip>
                                      }
                                    >
                                      <IconButton
                                        className={"p-0"}
                                        onClick={() => {
                                          track(
                                            ANALYTICS_PAGE_NAME,
                                            "share-form-button",
                                            "click",
                                          );
                                          setFormToShare(template.id);
                                          openContactSelector();
                                        }}
                                      >
                                        <OutlineShare className={"gray01"} />
                                      </IconButton>
                                    </OverlayTrigger>
                                  </td>
                                )}
                                <td className={styles["template-option-td"]}>
                                  <OverlayTrigger
                                    placement="top"
                                    overlay={
                                      <Tooltip id="button-tooltip">
                                        Delete
                                      </Tooltip>
                                    }
                                  >
                                    <IconButton
                                      onClick={() => {
                                        track(
                                          ANALYTICS_PAGE_NAME,
                                          `delete-${template.type}-button`,
                                          "click",
                                        );
                                        deleteSchemaMutation.mutate(
                                          {
                                            type: template.type,
                                            schemaId: template.id.toString(),
                                          },
                                          {
                                            onSuccess: () =>
                                              setSuccessMessage("Deleted!"),
                                            onError: setError,
                                          },
                                        );
                                      }}
                                      className="note-template"
                                    >
                                      <OutlineTrash color={`var(--gray01)`} />
                                    </IconButton>
                                  </OverlayTrigger>
                                </td>
                              </>
                            ) : (
                              <td className={styles["template-option-td"]}>
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip id="button-tooltip">View</Tooltip>
                                  }
                                >
                                  <Link
                                    to={{
                                      pathname: `/pro-dashboard/notes-and-forms/view-form-template/${template.id}`,
                                    }}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => {
                                      track(
                                        ANALYTICS_PAGE_NAME,
                                        "view-assessment-button",
                                        "click",
                                      );
                                    }}
                                  >
                                    <OutlineEyeOpen className={"gray01"} />
                                  </Link>
                                </OverlayTrigger>
                              </td>
                            )}
                          </tr>
                        ))}
                    </tbody>
                  </React.Fragment>
                ),
              )}
            </Table>
          )}
        </div>
      </div>
      <ContactSelector
        isForFormSharing={true}
        show={showContactSelector}
        onHide={closeContactSelector}
        onApply={handleContactSelected}
      />
      {error && (
        <ErrorMessage
          show={true}
          onHide={() => setError(undefined)}
          errorMsg={error.toString()}
        />
      )}
      {showAddTemplate && (
        <AddTemplateModal
          onApply={(mode) => {
            switch (mode) {
              case "custom":
                navigate(
                  `/pro-dashboard/notes-and-forms/edit-template/${
                    tab === "note-template" ? "note" : "form"
                  }`,
                );
                break;
              case "pre-built":
                setShowPrebuiltTemplates(true);
                break;
            }
            closeAddTemplate();
          }}
          onCancel={closeAddTemplate}
          show={showAddTemplate}
        />
      )}
    </>
  );
};

export default NotesAndForms;
